import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Map as MapIcon, ChevronFirst, ChevronLeft, ChevronRight, ChevronLast } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { assetApi, orgApi, unitTypeApi } from "@/lib/entities";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { SectionHeader } from "@/components/SectionHeader";
import { DemoChip, StatusPill } from "@/components/Chips";
import { RoleGate } from "@/components/RoleGate";
import { AssetFormDialog } from "@/pages/fleet/AssetFormDialog";
import { MobileListCard } from "@/components/MobileListCard";
import { ActionMenu, ArchiveConfirmModal, useArchive } from "@/components/ActionMenu";
import { assetDisplay } from "@/lib/assetDisplay";

// PRG · Wave 4B-6 canonical resolver. `can("fleet.edit")` auto-grants
// Standard Admin the same write capability that Super Admin,
// ops_dispatch, and maintenance have on the fleet roster.
const READ_ROLES = ["super_admin", "admin", "leasing_sales", "ops_dispatch", "accounting", "maintenance"];

const OPERATIONAL_STATUSES = [
  "available", "inquiry", "soft_hold", "first_hold", "second_hold",
  "contract_pending", "booked", "touring", "maintenance", "shop",
  "owner_use", "out_of_service", "inactive",
];

// RC10n · Fleet server-backed pagination constants
const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100", "all"];
const DEFAULT_PAGE_SIZE = "25";
const PAGE_SIZE_STORAGE_KEY = "allaccess.fleet.pageSize";

export default function FleetList() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();

  // ---- URL-backed state (RC10n) ----
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const pageSize = useMemo(() => {
    const raw = params.get("page_size");
    if (raw && PAGE_SIZE_OPTIONS.includes(raw)) return raw;
    const stored = (typeof window !== "undefined") ? window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY) : null;
    return (stored && PAGE_SIZE_OPTIONS.includes(stored)) ? stored : DEFAULT_PAGE_SIZE;
  }, [params]);
  const q = params.get("search") || "";
  const type = params.get("asset_type") || "";
  const status = params.get("operational_status") || "";
  const showArchived = params.get("include_archived") === "true";

  const [rows, setRows] = useState(null);
  const [envelope, setEnvelope] = useState({
    total_items: 0, total_pages: 1, has_previous: false, has_next: false,
    page: 1, page_size: DEFAULT_PAGE_SIZE, truncated: false, platform_cap: 5000,
  });
  const [orgs, setOrgs] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const canWrite = can("fleet.edit");

  const updateParams = useCallback((patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) {
        next.delete(k);
      } else {
        next.set(k, String(v));
      }
    });
    setParams(next, { replace: false });
  }, [params, setParams]);

  const load = useCallback(async () => {
    try {
      const d = await assetApi.list({
        search: q || undefined,
        asset_type: type || undefined,
        operational_status: status || undefined,
        include_archived: showArchived,
        page,
        page_size: pageSize,
      });
      const items = d.items || d.results || [];
      setRows(items);
      setEnvelope({
        total_items: d.total_items ?? d.total ?? 0,
        total_pages: d.total_pages ?? 1,
        has_previous: !!d.has_previous,
        has_next: !!d.has_next,
        page: d.page ?? page,
        page_size: d.page_size ?? pageSize,
        truncated: !!d.truncated,
        platform_cap: d.platform_cap ?? 5000,
      });
      // RC10n — if the current page is out of range (e.g. after a
      // destructive action), bounce to the highest valid page.
      const tp = d.total_pages ?? 1;
      if ((d.items || d.results || []).length === 0 && page > 1 && tp >= 1 && page > tp) {
        updateParams({ page: tp });
      }
    } catch (e) {
      toast.error("Failed to load fleet");
    }
  }, [q, page, pageSize, type, status, showArchived, updateParams]);

  const archive = useArchive({
    entityLabel: "Asset",
    apiArchive: assetApi.archive,
    onSuccess: async (row) => {
      toast.success(`Archived ${row.display_name || row.unit_name || row.fleet_number}`);
      await load();
    },
  });

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    orgApi.list({ page_size: 200 }).then((d) => setOrgs(d.results)).catch(() => {});
    unitTypeApi.list().then((r) => setUnitTypes(r || [])).catch(() => {});
  }, []);

  // Persist page size selection across sessions.
  useEffect(() => {
    try { window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize); } catch (_e) { /* ignore */ }
  }, [pageSize]);

  // Range label helpers
  const firstRow = rows && rows.length > 0 ? ((page - 1) * (pageSize === "all" ? envelope.total_items : parseInt(pageSize, 10))) + 1 : 0;
  const lastRow = firstRow + (rows?.length || 0) - 1;

  return (
    <RoleGate allow={READ_ROLES}>
      <PageShell>
        <SectionHeader testid="page-section-landmark" className="sr-only">Fleet Roster</SectionHeader>
        <PageHeader
          testid="page-fleet"
          title="Fleet"
          subtitle={`${envelope.total_items.toLocaleString()} assets`}
          right={
            <div className="flex items-center gap-2">
              <Link
                to="/fleet/map"
                data-testid="fleet-view-map"
                className="inline-flex items-center gap-1.5 border border-coach-silver hover:border-coach-blue text-carbon text-[12.5px] font-medium px-3 py-1.5 rounded-sm bg-white"
              >
                <MapIcon size={14} /> View Map
              </Link>
              {canWrite && (
                <button
                  data-testid="new-asset-btn"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-1.5 bg-coach-blue hover:bg-deep-road-navy text-white text-[12.5px] font-medium px-3 py-1.5 rounded-sm"
                >
                  <Plus size={14} /> New Asset
                </button>
              )}
            </div>
          }
        />

        <div className="bg-white border border-coach-silver rounded-md">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-coach-silver">
            <input
              data-testid="fleet-search"
              value={q}
              onChange={(e) => updateParams({ search: e.target.value, page: 1 })}
              placeholder="Search by fleet #, unit name, VIN…"
              className="flex-1 max-w-md border border-coach-silver rounded-sm px-3 py-1.5 text-[13px]"
            />
            <select
              data-testid="fleet-filter-type"
              value={type}
              onChange={(e) => updateParams({ asset_type: e.target.value, page: 1 })}
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[12.5px] bg-white"
            >
              <option value="">All types</option>
              {unitTypes.filter(t => !t.is_archived).map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
            <select
              data-testid="fleet-filter-status"
              value={status}
              onChange={(e) => updateParams({ operational_status: e.target.value, page: 1 })}
              className="border border-coach-silver rounded-sm px-2 py-1.5 text-[12.5px] bg-white"
            >
              <option value="">All statuses</option>
              {OPERATIONAL_STATUSES.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>
            <label className="text-[12px] text-steel flex items-center gap-1.5">
              <input
                type="checkbox"
                data-testid="fleet-show-archived"
                checked={showArchived}
                onChange={(e) => updateParams({ include_archived: e.target.checked, page: 1 })}
              />{" "}
              Include archived
            </label>
          </div>

          <table className="hidden md:table w-full text-[13px]" data-testid="fleet-table">
            <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
              <tr>
                <th className="text-left px-4 py-2">Name / Unit Number</th>
                <th className="text-left px-4 py-2 w-40">Type</th>
                <th className="text-left px-4 py-2">Year/Make/Model</th>
                <th className="text-left px-4 py-2 w-16">Bunks</th>
                <th className="text-left px-4 py-2 w-40">Status</th>
                <th className="text-right px-4 py-2 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`fleet-row-${r.fleet_number || r.id}`}
                  className="border-t border-coach-silver/60 hover:bg-road-white"
                >
                  <td className="px-4 py-2">
                    <Link to={`/fleet/${r.id}`} className="text-coach-blue hover:underline">
                      {assetDisplay(r)}
                    </Link>{" "}
                    {r.is_demo && <DemoChip />}
                  </td>
                  <td className="px-4 py-2 text-steel uppercase tracking-wide text-[11px]">{r.asset_type.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 text-steel">
                    {[r.year, r.make, r.model].filter(Boolean).join(" ") || "—"}
                  </td>
                  <td className="px-4 py-2 text-steel">{r.config?.bunk_count ?? "—"}</td>
                  <td className="px-4 py-2"><StatusPill status={r.operational_status} /></td>
                  <td className="px-2 py-2 text-right">
                    <ActionMenu
                      testid={`fleet-actions-${r.fleet_number || r.id}`}
                      onView={() => nav(`/fleet/${r.id}`)}
                      onEdit={canWrite ? () => nav(`/fleet/${r.id}?edit=1`) : undefined}
                      onArchive={canWrite && !r.is_archived ? () => archive.request({ ...r, display_name: assetDisplay(r) }) : undefined}
                    />
                  </td>
                </tr>
              ))}
              {rows === null && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-steel">
                    Loading fleet…
                  </td>
                </tr>
              )}
              {rows && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-steel">
                    No assets match.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3 p-3" data-testid="fleet-mobile-list">
            {rows === null && (
              <div className="text-center text-steel text-[13px] py-8">Loading fleet…</div>
            )}
            {rows && rows.length === 0 && (
              <div className="text-center text-steel text-[13px] py-8">No assets match.</div>
            )}
            {(rows || []).map((r) => (
              <MobileListCard
                key={r.id}
                to={`/fleet/${r.id}`}
                testid={`fleet-card-${r.fleet_number || r.id}`}
                primary={assetDisplay(r)}
                secondary={
                  <>
                    <span className="uppercase tracking-wide text-[11px]">{r.asset_type.replace(/_/g, " ")}</span>
                    {[r.year, r.make, r.model].filter(Boolean).length > 0 && (
                      <> · {[r.year, r.make, r.model].filter(Boolean).join(" ")}</>
                    )}
                    {r.config?.bunk_count && <> · {r.config.bunk_count} bunks</>}
                  </>
                }
                status={r.operational_status}
                isDemo={r.is_demo}
              />
            ))}
          </div>

          {/* RC10n · Pagination controls */}
          <FleetPaginationBar
            page={envelope.page}
            totalPages={envelope.total_pages}
            totalItems={envelope.total_items}
            pageSize={envelope.page_size}
            hasPrevious={envelope.has_previous}
            hasNext={envelope.has_next}
            firstRow={firstRow}
            lastRow={lastRow}
            truncated={envelope.truncated}
            platformCap={envelope.platform_cap}
            onPage={(p) => updateParams({ page: p })}
            onPageSize={(ps) => updateParams({ page_size: ps, page: 1 })}
          />
        </div>

        {open && (
          <AssetFormDialog
            open={open}
            orgs={orgs}
            onClose={() => setOpen(false)}
            onSaved={() => {
              setOpen(false);
              load();
            }}
          />
        )}
        <ArchiveConfirmModal {...archive.modalProps} />
      </PageShell>
    </RoleGate>
  );
}


function FleetPaginationBar({
  page, totalPages, totalItems, pageSize, hasPrevious, hasNext,
  firstRow, lastRow, truncated, platformCap, onPage, onPageSize,
}) {
  const showFirstLast = totalPages > 3;
  // RC10n — compact page-number set: current, +/-1, 1, totalPages.
  const pages = [];
  const push = (n) => { if (n >= 1 && n <= totalPages && !pages.includes(n)) pages.push(n); };
  push(1); push(page - 1); push(page); push(page + 1); push(totalPages);
  pages.sort((a, b) => a - b);
  return (
    <div
      data-testid="fleet-pagination"
      className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 px-4 py-3 border-t border-coach-silver bg-road-white rounded-b-md text-[12px]"
    >
      <div className="flex items-center gap-3">
        <span className="text-steel" data-testid="fleet-pagination-status">
          {totalItems === 0
            ? "No assets"
            : `Showing ${firstRow}\u2013${lastRow} of ${totalItems.toLocaleString()} assets`}
          {truncated && (
            <span className="ml-1 text-amber-hold">
              {" "}(platform cap {platformCap.toLocaleString()})
            </span>
          )}
        </span>
        <label className="flex items-center gap-1.5 text-steel">
          Rows per page
          <select
            data-testid="fleet-page-size-select"
            value={typeof pageSize === "string" ? pageSize : String(pageSize)}
            onChange={(e) => onPageSize(e.target.value)}
            className="border border-coach-silver rounded-sm px-2 py-0.5 text-[12px] bg-white"
          >
            {PAGE_SIZE_OPTIONS.map((o) => (
              <option key={o} value={o}>{o === "all" ? "All" : o}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex items-center gap-1">
        {showFirstLast && (
          <button
            type="button"
            disabled={!hasPrevious}
            onClick={() => onPage(1)}
            data-testid="fleet-page-first"
            className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
          ><ChevronFirst size={14} /></button>
        )}
        <button
          type="button"
          disabled={!hasPrevious}
          onClick={() => onPage(page - 1)}
          data-testid="fleet-page-prev"
          className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
        ><ChevronLeft size={14} /></button>
        {pages.map((n, idx) => {
          const prev = pages[idx - 1];
          const gap = prev !== undefined && n - prev > 1;
          return (
            <React.Fragment key={n}>
              {gap && <span className="px-1 text-steel">…</span>}
              <button
                type="button"
                onClick={() => onPage(n)}
                disabled={n === page}
                data-testid={`fleet-page-${n}`}
                className={`px-2 py-0.5 rounded-sm border text-[12px] ${
                  n === page
                    ? "bg-coach-blue text-white border-coach-blue"
                    : "bg-white border-coach-silver hover:bg-coach-silver/20"
                }`}
              >{n}</button>
            </React.Fragment>
          );
        })}
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onPage(page + 1)}
          data-testid="fleet-page-next"
          className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
        ><ChevronRight size={14} /></button>
        {showFirstLast && (
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => onPage(totalPages)}
            data-testid="fleet-page-last"
            className="px-1.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 disabled:opacity-40"
          ><ChevronLast size={14} /></button>
        )}
      </div>
    </div>
  );
}
